import { FC, JSX } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Typography,
  Stack,
} from '@mui/joy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import LanguageIcon from '@mui/icons-material/Language';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { Input } from '@consolidocs-ui/components';
import { CompanyType } from '@consolidocs-ui/types';
import { ProvisionFlag } from '@consolidocs-ui/hooks/useEnterpriseOrgStatus';

type Errors = Partial<Record<keyof CompanyType, string>>;

interface Step2Props {
  formData: CompanyType;
  errors: Errors;
  onChange: (field: keyof CompanyType, value: unknown) => void;
  /** Edit mode renders identifiers read-only. */
  isEdit?: boolean;
  /** Live provisioning flags from `GET /enterprise-org-status`. */
  flags?: Record<ProvisionFlag, boolean>;
  /** True while the status endpoint is still being polled. */
  isPolling?: boolean;
  /** True when polling stopped before every resource was ready. */
  isTimedOut?: boolean;
  /** True when the status endpoint itself could not be read. */
  hasStatusError?: boolean;
  /** True once the create request has been accepted by the backend. */
  isCreated?: boolean;
  /** True when the infrastructure build failed and can be retried. */
  isFailed?: boolean;
  /** Backend provisioning status, shown alongside a failure. */
  provisioningStatus?: string;
  /** Starts a fresh infrastructure build. */
  onRetry?: () => void;
  /** True while the retry request is in flight. */
  isRetrying?: boolean;
}

// The four backend-managed resources provisioned when an organization is
// created. They are never edited by hand — the UI only reflects their state.
const PROVISION_STEPS: { key: ProvisionFlag; label: string }[] = [
  { key: 'isDbCreated', label: 'Database' },
  { key: 'isCognitoPoolCreated', label: 'Cognito user pool' },
  { key: 'isSqsReady', label: 'SQS queue' },
  { key: 'isServiceReady', label: 'Service' },
];

interface ProvisionListProps {
  flags: Record<ProvisionFlag, boolean>;
  /** Renders the first unfinished resource as in-progress rather than pending. */
  isPolling: boolean;
}

/**
 * Provisioning state driven entirely by the backend flags. The first resource
 * that is not ready yet is shown as in-progress while we are still polling,
 * which is what makes the wait read as real progress.
 */
export const ProvisionList: FC<ProvisionListProps> = ({
  flags,
  isPolling,
}): JSX.Element => {
  const firstPendingIndex = PROVISION_STEPS.findIndex(
    (step) => !flags[step.key]
  );

  return (
    <Stack className="provision-list">
      {PROVISION_STEPS.map((step, index) => {
        const done = flags[step.key];
        const active = isPolling && index === firstPendingIndex;
        const state = done ? 'done' : active ? 'active' : 'pending';

        return (
          <Box
            key={step.key}
            className={`provision-step provision-step--${state}`}
          >
            <span className="provision-step__icon">
              {done ? (
                <CheckCircleRoundedIcon
                  className="provision-check"
                  color="success"
                />
              ) : active ? (
                <CircularProgress size="sm" />
              ) : (
                <RadioButtonUncheckedRoundedIcon />
              )}
            </span>
            <Typography level="body-sm" className="provision-step__label">
              {step.label}
            </Typography>
            <Chip
              size="sm"
              variant="soft"
              color={done ? 'success' : active ? 'primary' : 'neutral'}
              sx={{ ml: 'auto' }}
            >
              {done ? 'Complete' : active ? 'In progress' : 'Pending'}
            </Chip>
          </Box>
        );
      })}
    </Stack>
  );
};

const emptyFlags: Record<ProvisionFlag, boolean> = {
  isDbCreated: false,
  isCognitoPoolCreated: false,
  isSqsReady: false,
  isServiceReady: false,
};

const Step2InfrastructureConfig: FC<Step2Props> = ({
  formData,
  errors,
  onChange,
  isEdit = false,
  flags = emptyFlags,
  isPolling = false,
  isTimedOut = false,
  hasStatusError = false,
  isCreated = false,
  isFailed = false,
  provisioningStatus,
  onRetry,
  isRetrying = false,
}): JSX.Element => {
  // Identifiers are locked once the organization exists — on edit, and as soon
  // as the create request has gone through.
  const isLocked = isEdit || isCreated;

  const renderTextField = (
    field: keyof CompanyType,
    label: string,
    placeholder?: string
  ) => (
    <Input
      key={field as string}
      label={label}
      name={field as string}
      autoComplete="off"
      value={(formData[field] as string) || ''}
      placeholder={placeholder}
      readOnly={isLocked}
      disabled={isLocked}
      onChange={(e) => onChange(field, e.target.value)}
      error={!!errors[field]}
      errorMessage={errors[field] as string | undefined}
    />
  );

  // Show the resource list whenever the organization exists; before that there
  // is nothing to report, so we only explain what will happen.
  const showStatus = isEdit || isCreated;

  return (
    <Box className="wizard-step infra-step">
      {/* Editable tenant identifiers */}
      <Box className="infra-section">
        <Box className="infra-section-header">
          <LanguageIcon fontSize="small" />
          <Typography level="title-sm">Tenant identifiers</Typography>
        </Box>
        <Typography level="body-xs" className="infra-section-hint">
          Both fields are optional and must be unique across all organizations.
        </Typography>
        <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderTextField('subdomain', 'Subdomain', 'e.g. acme')}
          {renderTextField('slug', 'Organization slug', 'e.g. acme-corp')}
        </Box>
      </Box>

      <Divider />

      {/* Provisioning: live backend status once the org exists, a short
          explainer before that. Never editable by hand. */}
      <Card variant="outlined" className="infra-status-card">
        <Box className="infra-section-header">
          <SettingsSuggestIcon fontSize="small" />
          <Typography level="title-sm">
            {showStatus ? 'Provisioning status' : 'Automated setup'}
          </Typography>
        </Box>

        {showStatus ? (
          <>
            {isPolling && (
              <Typography level="body-xs" className="infra-section-hint">
                Setting up your organization — this can take a few minutes.
              </Typography>
            )}
            <ProvisionList flags={flags} isPolling={isPolling} />
            {isTimedOut && (
              <Alert
                variant="soft"
                color="warning"
                startDecorator={<HourglassTopRoundedIcon />}
              >
                Setup is taking longer than usual. The organization has been
                created and provisioning continues in the background — you can
                safely leave this page and check back later.
              </Alert>
            )}
            {isFailed && (
              <Alert
                variant="soft"
                color="danger"
                startDecorator={<ErrorOutlineRoundedIcon />}
                endDecorator={
                  onRetry && (
                    <Button
                      size="sm"
                      variant="solid"
                      color="danger"
                      startDecorator={<ReplayRoundedIcon />}
                      loading={isRetrying}
                      onClick={onRetry}
                    >
                      Retry
                    </Button>
                  )
                }
              >
                Infrastructure setup failed
                {provisioningStatus ? ` (${provisioningStatus})` : ''}. The
                organization was saved — retry to start setup again.
              </Alert>
            )}
            {hasStatusError && (
              <Alert
                variant="soft"
                color="danger"
                startDecorator={<HourglassTopRoundedIcon />}
              >
                Couldn&apos;t read the provisioning status. The organization
                itself was saved — reopen it later to see where setup got to.
              </Alert>
            )}
          </>
        ) : (
          <Alert
            variant="soft"
            color="primary"
            startDecorator={<AutoAwesomeRoundedIcon />}
          >
            Organization database, Cognito user pool, SQS queue and service are
            provisioned automatically when you create the organization.
          </Alert>
        )}
      </Card>
    </Box>
  );
};

export default Step2InfrastructureConfig;
